/**
 * Ant-specific WebGL Renderer
 *
 * Wraps WebGLSpriteRenderer with ant-specific sprite sheet generation
 * and provides a simple API for rendering ants.
 */

import { WebGLSpriteRenderer, SpriteSheetConfig, SpriteInstance } from './WebGLSpriteRenderer';
import { AntRole } from '@antification/core';

// Number of rotation steps (must match sprite generation)
const ANGLE_STEPS = 16;
const ANGLE_STEP_SIZE = (Math.PI * 2) / ANGLE_STEPS;

// Role order in sprite sheet (must match generation order)
const FORMICARIUM_ROLES = ['queen', 'forager', 'soldier', 'nurse', 'engineer', 'selected', 'hovered'];
const SURFACE_COLONY_COUNT = 3;

/**
 * Color definitions for generating ant sprites
 */
const FORMICARIUM_COLORS: Record<string, { color: string; darkerColor: string }> = {
  queen: { color: '#b8860b', darkerColor: '#8b6508' },
  forager: { color: '#8b6914', darkerColor: '#5a4510' },
  soldier: { color: '#654321', darkerColor: '#4a3018' },
  nurse: { color: '#7a6520', darkerColor: '#5a4510' },
  engineer: { color: '#6b5a4a', darkerColor: '#4a3a2a' },
  selected: { color: '#ffd700', darkerColor: '#b8960b' },
  hovered: { color: '#daa520', darkerColor: '#a07810' },
};

const SURFACE_COLORS = [
  { body: '#2a1810', outline: '#1a0d08' },
  { body: '#1a2020', outline: '#0a1010' },
  { body: '#3a1010', outline: '#200808' },
];

export class AntWebGLRenderer {
  private renderer: WebGLSpriteRenderer;
  private formicariumSheet: SpriteSheetConfig | null = null;
  private surfaceSheet: SpriteSheetConfig | null = null;
  private initialized = false;

  // Sprite dimensions - rendered at higher resolution for detail when zoomed
  private readonly formicariumRefScale = 4;
  private readonly formicariumSpriteWidth = 36 * 4;   // 144px (4x resolution)
  private readonly formicariumSpriteHeight = 32 * 4;  // 128px (4x resolution)
  private readonly surfaceSpriteSize = 80;     // pixels (rendered at 10x scale)

  // Offset and scale for coordinate transformation
  private offsetX = 0;
  private offsetY = 0;
  private scale = 1;

  constructor() {
    this.renderer = new WebGLSpriteRenderer(3000); // Support up to 3000 ants
  }

  /**
   * Initialize the renderer. Must be called once.
   * Returns the canvas element to attach to the DOM.
   */
  initialize(): HTMLCanvasElement | null {
    if (!this.renderer.initialize()) {
      return null;
    }

    // Generate sprite sheets
    this.formicariumSheet = this.generateFormicariumSpriteSheet();
    this.surfaceSheet = this.generateSurfaceSpriteSheet();

    if (!this.formicariumSheet || !this.surfaceSheet) {
      console.error('[AntWebGLRenderer] Failed to create sprite sheets');
      return null;
    }

    this.initialized = true;
    console.log('[AntWebGLRenderer] Initialized successfully');

    return this.renderer.getCanvas();
  }

  /**
   * Generate sprite sheet for formicarium ants.
   * Layout: rows = roles (7), columns = angles (16)
   */
  private generateFormicariumSpriteSheet(): SpriteSheetConfig | null {
    const sprites: HTMLCanvasElement[] = [];
    const size = 4 * this.formicariumRefScale; // Base ant size * resolution scale

    for (const roleKey of FORMICARIUM_ROLES) {
      const colors = FORMICARIUM_COLORS[roleKey];
      for (let angleIdx = 0; angleIdx < ANGLE_STEPS; angleIdx++) {
        const angle = angleIdx * ANGLE_STEP_SIZE;
        sprites.push(this.renderFormicariumAnt(colors, angle, size));
      }
    }

    return this.renderer.createSpriteSheet(
      sprites,
      this.formicariumSpriteWidth,
      this.formicariumSpriteHeight
    );
  }

  /**
   * Generate sprite sheet for surface ants.
   * Layout: rows = colonies (3), columns = angles (16)
   */
  private generateSurfaceSpriteSheet(): SpriteSheetConfig | null {
    const sprites: HTMLCanvasElement[] = [];
    const spriteSize = this.surfaceSpriteSize;

    for (let colonyIdx = 0; colonyIdx < SURFACE_COLONY_COUNT; colonyIdx++) {
      const colors = SURFACE_COLORS[colonyIdx];
      for (let angleIdx = 0; angleIdx < ANGLE_STEPS; angleIdx++) {
        const angle = angleIdx * ANGLE_STEP_SIZE;
        sprites.push(this.renderSurfaceAnt(colors, angle, spriteSize));
      }
    }

    return this.renderer.createSpriteSheet(sprites, spriteSize, spriteSize);
  }

  private renderFormicariumAnt(
    colors: { color: string; darkerColor: string },
    angle: number,
    size: number
  ): HTMLCanvasElement {
    const rs = this.formicariumRefScale;
    const canvas = document.createElement('canvas');
    canvas.width = this.formicariumSpriteWidth;
    canvas.height = this.formicariumSpriteHeight;
    const ctx = canvas.getContext('2d')!;

    const x = this.formicariumSpriteWidth / 2;
    const y = this.formicariumSpriteHeight / 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    ctx.fillStyle = colors.color;
    ctx.strokeStyle = colors.darkerColor;
    ctx.lineWidth = 0.5 * rs;

    // Abdomen
    const abdomenX = x - cos * size * 1.3;
    const abdomenY = y - sin * size * 1.3;
    ctx.beginPath();
    ctx.ellipse(abdomenX, abdomenY, size * 1.2, size * 0.8, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Thorax
    ctx.beginPath();
    ctx.ellipse(x, y, size * 0.8, size * 0.55, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Head
    const headX = x + cos * size * 1.1;
    const headY = y + sin * size * 1.1;
    ctx.beginPath();
    ctx.ellipse(headX, headY, size * 0.55, size * 0.45, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Antennae
    ctx.strokeStyle = colors.color;
    ctx.lineWidth = 0.7 * rs;
    const antennaLen = size * 1.0;
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle - 0.6) * antennaLen, headY + Math.sin(angle - 0.6) * antennaLen);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle + 0.6) * antennaLen, headY + Math.sin(angle + 0.6) * antennaLen);
    ctx.stroke();

    // Legs
    ctx.strokeStyle = colors.darkerColor;
    ctx.lineWidth = 0.5 * rs;
    const legLen = size * 0.9;
    for (let i = -1; i <= 1; i++) {
      const legBaseX = x + cos * size * i * 0.4;
      const legBaseY = y + sin * size * i * 0.4;
      const leftAngle = angle - Math.PI / 2 - 0.3 + i * 0.2;
      ctx.beginPath();
      ctx.moveTo(legBaseX, legBaseY);
      ctx.lineTo(legBaseX + Math.cos(leftAngle) * legLen, legBaseY + Math.sin(leftAngle) * legLen);
      ctx.stroke();
      const rightAngle = angle + Math.PI / 2 + 0.3 - i * 0.2;
      ctx.beginPath();
      ctx.moveTo(legBaseX, legBaseY);
      ctx.lineTo(legBaseX + Math.cos(rightAngle) * legLen, legBaseY + Math.sin(rightAngle) * legLen);
      ctx.stroke();
    }

    return canvas;
  }

  private renderSurfaceAnt(
    colors: { body: string; outline: string },
    angle: number,
    spriteSize: number
  ): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = spriteSize;
    canvas.height = spriteSize;
    const ctx = canvas.getContext('2d')!;

    // Render at 10x scale for quality
    const refScale = 10;
    const size = 1 * refScale;

    const x = spriteSize / 2;
    const y = spriteSize / 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    ctx.fillStyle = colors.body;
    ctx.strokeStyle = colors.outline;
    ctx.lineWidth = 0.5 * refScale;

    // Abdomen
    const abdomenX = x - cos * size * 1.2;
    const abdomenY = y - sin * size * 1.2;
    ctx.beginPath();
    ctx.ellipse(abdomenX, abdomenY, size * 1.1, size * 0.7, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Thorax
    ctx.beginPath();
    ctx.ellipse(x, y, size * 0.7, size * 0.5, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Head
    const headX = x + cos * size * 1.0;
    const headY = y + sin * size * 1.0;
    ctx.beginPath();
    ctx.ellipse(headX, headY, size * 0.5, size * 0.4, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Antennae
    ctx.strokeStyle = colors.body;
    ctx.lineWidth = 0.5 * refScale;
    const antennaLen = size * 0.8;
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle - 0.5) * antennaLen, headY + Math.sin(angle - 0.5) * antennaLen);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle + 0.5) * antennaLen, headY + Math.sin(angle + 0.5) * antennaLen);
    ctx.stroke();

    return canvas;
  }

  /**
   * Resize the renderer canvas.
   */
  resize(width: number, height: number, pixelRatio = 1): void {
    this.renderer.resize(width, height, pixelRatio);
  }

  /**
   * Set coordinate transformation (matches Canvas 2D transform).
   */
  setTransform(offsetX: number, offsetY: number, scale: number): void {
    this.offsetX = offsetX;
    this.offsetY = offsetY;
    this.scale = scale;
  }

  /**
   * Begin a new frame for formicarium ants.
   */
  beginFormicariumFrame(): void {
    if (!this.formicariumSheet) return;
    this.renderer.setSpriteSheet(this.formicariumSheet);
    this.renderer.beginFrame();
  }

  /**
   * Begin a new frame for surface ants.
   */
  beginSurfaceFrame(): void {
    if (!this.surfaceSheet) return;
    this.renderer.setSpriteSheet(this.surfaceSheet);
    this.renderer.beginFrame();
  }

  /**
   * Add a formicarium ant to render.
   */
  addFormicariumAnt(
    x: number,
    y: number,
    angle: number,
    role: AntRole | 'selected' | 'hovered',
    alpha = 1
  ): void {
    // Transform coordinates
    const screenX = x * this.scale + this.offsetX;
    const screenY = y * this.scale + this.offsetY;

    // Get sprite index
    const roleIdx = FORMICARIUM_ROLES.indexOf(role);
    const angleIdx = this.quantizeAngle(angle);
    const spriteIndex = roleIdx * ANGLE_STEPS + angleIdx;

    // Sprites are rendered at formicariumRefScale resolution; scale down to world size
    this.renderer.addSprite({
      x: screenX,
      y: screenY,
      rotation: 0, // Rotation is baked into the sprite
      scale: this.scale / this.formicariumRefScale,
      spriteIndex,
      alpha,
    });
  }

  /**
   * Add a surface ant to render.
   */
  addSurfaceAnt(
    screenX: number,
    screenY: number,
    angle: number,
    colonyIndex: number,
    scale: number,
    alpha = 1
  ): void {
    const safeColonyIdx = Math.min(colonyIndex, SURFACE_COLONY_COUNT - 1);
    const angleIdx = this.quantizeAngle(angle);
    const spriteIndex = safeColonyIdx * ANGLE_STEPS + angleIdx;

    // Surface sprites are rendered at 10x, need to scale down
    const drawScale = scale / 10;

    this.renderer.addSprite({
      x: screenX,
      y: screenY,
      rotation: 0,
      scale: drawScale,
      spriteIndex,
      alpha,
    });
  }

  private quantizeAngle(angle: number): number {
    let normalized = angle % (Math.PI * 2);
    if (normalized < 0) normalized += Math.PI * 2;
    return Math.round(normalized / ANGLE_STEP_SIZE) % ANGLE_STEPS;
  }

  /**
   * Finish rendering and flush to screen.
   */
  endFrame(): void {
    this.renderer.endFrame();
  }

  /**
   * Get number of sprites queued this frame.
   */
  getInstanceCount(): number {
    return this.renderer.getInstanceCount();
  }

  /**
   * Check if renderer is ready.
   */
  isReady(): boolean {
    return this.initialized && this.renderer.isReady();
  }

  /**
   * Get the canvas element.
   */
  getCanvas(): HTMLCanvasElement {
    return this.renderer.getCanvas();
  }

  /**
   * Get debug info.
   */
  getDebugInfo(): object {
    const canvas = this.renderer.getCanvas();
    return {
      initialized: this.initialized,
      isReady: this.isReady(),
      formicariumSheetReady: !!this.formicariumSheet,
      surfaceSheetReady: !!this.surfaceSheet,
      canvasSize: {
        width: canvas.width,
        height: canvas.height,
        cssWidth: canvas.style.width,
        cssHeight: canvas.style.height,
      },
      transform: {
        offsetX: this.offsetX,
        offsetY: this.offsetY,
        scale: this.scale,
      },
      spriteSize: {
        formicariumWidth: this.formicariumSpriteWidth,
        formicariumHeight: this.formicariumSpriteHeight,
        formicariumRefScale: this.formicariumRefScale,
        surfaceSize: this.surfaceSpriteSize,
      },
      instanceCount: this.renderer.getInstanceCount(),
    };
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    this.renderer.dispose();
  }
}

/**
 * Lazily-constructed singleton.
 *
 * This was `export const antWebGLRenderer = new AntWebGLRenderer()`, which
 * called `document.createElement('canvas')` at *module load*. Importing
 * FormicariumLayer therefore required a DOM before a single line of simulation
 * ran, which is fatal to headless settlement and was invisible in the browser.
 * Constructing on first use costs nothing and unblocks Node.
 */
let instance: AntWebGLRenderer | null = null;

export function getAntWebGLRenderer(): AntWebGLRenderer {
  if (!instance) instance = new AntWebGLRenderer();
  return instance;
}
